// PomelaCheckout — the REAL embedded Pomela checkout (§LG2). PayPal is the processor;
// the experience lives INSIDE Pomela: the official PayPal JS SDK renders its own
// buttons (PayPal + PayPal-hosted card) in our modal, order/subscription creation is
// SERVER-side, and success is shown ONLY after the SERVER verifies the capture (top-up)
// or the subscription is visible (Pro).
//
// HONESTY CONTRACT (fake-payment-ui-guard pins these):
//   • No card inputs of our own — card entry is PayPal-hosted (its own button + sheet).
//   • No success state without server proof (capture ok / alreadyProcessed / plan flip).
//   • No client-side credit math — packages + prices come from the server config
//     endpoint (canonical TOPUP_PACKAGES), and the capture grant is server-validated.
//   • Apple Pay / Google Pay are NOT rendered: enabling them through PayPal requires
//     account eligibility + dashboard onboarding + domain registration (Apple Pay also
//     a /.well-known domain-association file). Until that external onboarding is done
//     we never show a wallet button we can't honor. (BLOCKED_EXTERNAL_DEPENDENCY.)
//   • If the SDK can't load (offline/adblock), the fallback is the honest REDIRECT to
//     PayPal — clearly labelled — never a simulated form.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { X, Loader2, Check, TriangleAlert, ExternalLink, ShieldCheck } from 'lucide-preact';
import { useToast } from '@vibeshell/hooks';
import { getDeviceId } from '../lib/deviceId';
import { t } from '../lib/i18n';

// ─── types ───────────────────────────────────────────────────────────────────

export type CheckoutMode = 'topup' | 'pro';

interface ClientConfig {
  ok: boolean;
  clientId: string;
  env: 'live' | 'sandbox';
  currency: string;
  packages: Array<{ credits: number; priceUsd: number }>;
  proPriceUsd: number;
}

/** Result reported to the host surface once checkout truly finished (server-verified). */
export interface CheckoutDone {
  kind: 'topup-granted' | 'topup-already' | 'pro-active' | 'pro-webhook-pending' | 'mock-upgraded';
  credited?: number;
  remaining?: number;
}

type State =
  | { k: 'config' }                      // loading /v1/billing/paypal-client-config
  | { k: 'sdk' }                         // loading the PayPal JS SDK
  | { k: 'ready' }                       // buttons rendered, awaiting buyer
  | { k: 'fallback'; reason: string }    // SDK unavailable → honest redirect option
  | { k: 'verifying' }                   // approval received → SERVER capture/verify in flight
  | { k: 'success'; done: CheckoutDone } // server-verified success
  | { k: 'webhook-pending' }             // payment approved; plan flip still propagating
  | { k: 'failed'; detail: string }      // payment did not complete (no grant)
  | { k: 'support' }                     // captured but state not visible → support path
  | { k: 'error'; detail: string };      // config/SDK/server error before payment

// Minimal surface of the PayPal Buttons SDK we use.
interface PayPalButtonsNs {
  Buttons: (opts: Record<string, unknown>) => { render: (el: HTMLElement) => Promise<void>; close?: () => void };
}

// ─── SDK loader (cached per namespace) ──────────────────────────────────────

const sdkCache = new Map<string, Promise<PayPalButtonsNs>>();

function loadPayPalSdk(clientId: string, mode: CheckoutMode, env: 'live' | 'sandbox'): Promise<PayPalButtonsNs> {
  // The namespace is also the SDK cache key, so it must include the environment. Keying on mode
  // alone would let a live SDK loaded earlier in the session be reused after the server switched
  // to sandbox — silently defeating the host fix below.
  const ns = `${mode === 'pro' ? 'paypal_pomela_pro' : 'paypal_pomela_topup'}_${env}`;
  const cached = sdkCache.get(ns);
  if (cached) return cached;
  const params = new URLSearchParams({
    'client-id': clientId,
    currency: 'USD',
    components: 'buttons',
    intent: mode === 'pro' ? 'subscription' : 'capture',
    // PayPal-hosted card stays available to guests; BNPL/venmo hidden to keep
    // the surface to the methods we actually support today.
    'enable-funding': 'card',
    'disable-funding': 'paylater,credit,venmo'
  });
  if (mode === 'pro') params.set('vault', 'true');
  const p = new Promise<PayPalButtonsNs>((resolve, reject) => {
    const s = document.createElement('script');
    // §SDK-HOST-MUST-MATCH-SERVER. This was hardcoded to the LIVE host regardless of `env`, even
    // though the server already returns which environment it is operating in and this component
    // already fetched that field — it was simply never used. The 2026-08-23 reset measured the
    // consequence: with PAYPAL_ENV set to sandbox, the server refused every PayPal call while the
    // browser still loaded the LIVE SDK with a LIVE client id (proven by PayPal's own validator —
    // the sandbox host rejects that credential with 400, the live host accepts it with 200).
    // A checkout surface that says "sandbox" server-side and loads live client-side is a latent
    // live-money path that no env flag governs.
    const host = env === 'live' ? 'https://www.paypal.com' : 'https://www.sandbox.paypal.com';
    s.src = `${host}/sdk/js?${params.toString()}`;
    s.async = true;
    s.setAttribute('data-namespace', ns);
    s.onload = () => {
      const api = (window as unknown as Record<string, PayPalButtonsNs | undefined>)[ns];
      if (api?.Buttons) resolve(api);
      else reject(new Error('sdk_namespace_missing'));
    };
    s.onerror = () => reject(new Error('sdk_load_failed'));
    document.head.appendChild(s);
  });
  sdkCache.set(ns, p);
  p.catch(() => sdkCache.delete(ns)); // allow retry after a transient failure
  return p;
}

// ─── server helpers ──────────────────────────────────────────────────────────

/** Distinguishes "switched off" from "transient failure" — they deserve different copy. */
let paymentsDisabled = false;

async function fetchConfig(): Promise<ClientConfig | null> {
  try {
    const r = await fetch('/v1/billing/paypal-client-config', { credentials: 'include' });
    // §PAYMENTS-KILL-SWITCH — the server distinguishes "payments are switched off on this
    // deployment" from "something went wrong". Telling someone to "try again shortly" when the
    // capability is deliberately disabled is a false promise: retrying can never succeed.
    if (r.status === 503) {
      const j = (await r.json().catch(() => ({}))) as { error?: string; enabled?: boolean };
      if (j.error === 'payments_disabled' || j.enabled === false) paymentsDisabled = true;
      return null;
    }
    if (!r.ok) return null;
    const j = (await r.json()) as ClientConfig;
    return j.ok && j.clientId ? j : null;
  } catch {
    return null;
  }
}

async function fetchCreditsRemaining(): Promise<{ remaining: number; tier: string } | null> {
  try {
    const r = await fetch('/v1/credits', { credentials: 'include' });
    if (!r.ok) return null;
    const j = (await r.json()) as { tier?: string; credits?: { remaining?: number } };
    return { remaining: j.credits?.remaining ?? 0, tier: j.tier ?? 'free' };
  } catch {
    return null;
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ─── the checkout body (host-agnostic content) ───────────────────────────────

export function PomelaCheckoutBody(props: {
  mode: CheckoutMode;
  onDone: (done: CheckoutDone) => void;
  onClose: () => void;
}): JSX.Element {
  const { mode } = props;
  const [state, setState] = useState<State>({ k: 'config' });
  const [cfg, setCfg] = useState<ClientConfig | null>(null);
  const [pkgIdx, setPkgIdx] = useState(0);
  const btnHost = useRef<HTMLDivElement>(null);
  const pkgIdxRef = useRef(0);
  pkgIdxRef.current = pkgIdx;
  const doneRef = useRef(false);

  // 1) config → 2) SDK → 3) render PayPal's own buttons.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const config = await fetchConfig();
      if (cancelled) return;
      if (!config) {
        setState({ k: 'error', detail: paymentsDisabled ? t('pc.disabled') : t('pc.unavailable') });
        return;
      }
      setCfg(config);
      setState({ k: 'sdk' });
      let sdk: PayPalButtonsNs;
      try {
        sdk = await loadPayPalSdk(config.clientId, mode, config.env === 'live' ? 'live' : 'sandbox');
      } catch {
        if (!cancelled) setState({ k: 'fallback', reason: 'sdk' });
        return;
      }
      if (cancelled) return;
      setState({ k: 'ready' });
      // Render after the host div exists.
      await sleep(0);
      const host = btnHost.current;
      if (!host || cancelled) return;

      const common = {
        style: { layout: 'vertical', shape: 'rect', label: 'pay' },
        onError: () => {
          if (!doneRef.current) setState({ k: 'error', detail: t('pc.sdkError') });
        },
        onCancel: () => {
          if (!doneRef.current) setState({ k: 'ready' });
        }
      };

      const buttons =
        mode === 'topup'
          ? sdk.Buttons({
              ...common,
              // SERVER creates the order for the selected canonical package.
              createOrder: async () => {
                const credits = (cfg ?? config).packages[pkgIdxRef.current]?.credits;
                const r = await fetch('/v1/credits/topup', {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'content-type': 'application/json' },
                  body: JSON.stringify({ credits })
                });
                const j = (await r.json().catch(() => ({}))) as { orderId?: string; error?: string };
                if (!r.ok || !j.orderId) throw new Error(j.error ?? 'order_failed');
                return j.orderId;
              },
              // SERVER captures + grants (idempotent). Success ONLY from the server's answer.
              onApprove: async (data: { orderID?: string }) => {
                setState({ k: 'verifying' });
                try {
                  const r = await fetch('/v1/credits/topup/capture', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ orderId: data.orderID })
                  });
                  const j = (await r.json().catch(() => ({}))) as {
                    ok?: boolean; credited?: number; alreadyProcessed?: boolean; status?: string;
                    credits?: { remaining?: number };
                  };
                  if (r.ok && j.ok) {
                    doneRef.current = true;
                    const done: CheckoutDone = j.alreadyProcessed
                      ? { kind: 'topup-already', remaining: j.credits?.remaining }
                      : { kind: 'topup-granted', credited: j.credited, remaining: j.credits?.remaining };
                    setState({ k: 'success', done });
                    props.onDone(done);
                    return;
                  }
                  if (r.status === 402) setState({ k: 'failed', detail: j.status ?? 'incomplete' });
                  else setState({ k: 'support' });
                } catch {
                  // The capture may have landed server-side even if our read failed.
                  setState({ k: 'support' });
                }
              }
            })
          : sdk.Buttons({
              ...common,
              // SERVER creates the Pro subscription; the SDK opens PayPal's approval.
              createSubscription: async () => {
                const r = await fetch('/v1/billing/checkout', {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'content-type': 'application/json' },
                  body: JSON.stringify({ deviceId: getDeviceId() })
                });
                const j = (await r.json().catch(() => ({}))) as {
                  url?: string; subscriptionId?: string; mock?: boolean; error?: string;
                };
                if (j.mock) {
                  // Dev/staging mock path: the server already upgraded the account —
                  // report an honest mock upgrade; nothing to approve at PayPal.
                  doneRef.current = true;
                  const done: CheckoutDone = { kind: 'mock-upgraded' };
                  setState({ k: 'success', done });
                  props.onDone(done);
                  throw new Error('mock_upgraded'); // aborts the SDK flow; state already final
                }
                if (!r.ok || !j.subscriptionId) throw new Error(j.error ?? 'checkout_failed');
                return j.subscriptionId;
              },
              onApprove: async (data: { subscriptionID?: string }) => {
                setState({ k: 'verifying' });
                // Verify with OUR server: subscription visible → then wait for the
                // signature-verified webhook to flip the plan (enforcement truth).
                try {
                  const subId = data.subscriptionID ?? '';
                  let subOk = false;
                  for (let i = 0; i < 5 && !subOk; i++) {
                    const r = await fetch(`/v1/billing/paypal/subscription/${encodeURIComponent(subId)}`, { credentials: 'include' });
                    if (r.ok) {
                      const j = (await r.json().catch(() => ({}))) as { status?: string };
                      if (j.status === 'ACTIVE' || j.status === 'APPROVED') subOk = true;
                    }
                    if (!subOk) await sleep(2000);
                  }
                  for (let i = 0; i < 10; i++) {
                    const cr = await fetchCreditsRemaining();
                    if (cr?.tier === 'pro') {
                      doneRef.current = true;
                      const done: CheckoutDone = { kind: 'pro-active' };
                      setState({ k: 'success', done });
                      props.onDone(done);
                      return;
                    }
                    await sleep(2000);
                  }
                  // Approved but the flip hasn't propagated yet — honest pending, never fake success.
                  doneRef.current = true;
                  setState({ k: 'webhook-pending' });
                  props.onDone({ kind: 'pro-webhook-pending' });
                } catch {
                  setState({ k: 'webhook-pending' });
                }
              }
            });

      try {
        await buttons.render(host);
      } catch {
        if (!cancelled && !doneRef.current) setState({ k: 'fallback', reason: 'render' });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Honest REDIRECT fallback (SDK unavailable). Top-up returns are completed by
  // TopupReturnCapture (the server appends ?pptopup=success&token=<orderId>).
  async function fallbackRedirect(): Promise<void> {
    setState({ k: 'verifying' });
    try {
      if (mode === 'topup') {
        const credits = cfg?.packages[pkgIdxRef.current]?.credits;
        const r = await fetch('/v1/credits/topup', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ credits })
        });
        const j = (await r.json().catch(() => ({}))) as { approvalUrl?: string };
        if (j.approvalUrl) { window.location.href = j.approvalUrl; return; }
      } else {
        const r = await fetch('/v1/billing/checkout', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ deviceId: getDeviceId() })
        });
        const j = (await r.json().catch(() => ({}))) as { url?: string; mock?: boolean };
        if (j.mock) { props.onDone({ kind: 'mock-upgraded' }); return; }
        if (j.url) { window.location.href = j.url; return; }
      }
      setState({ k: 'error', detail: t('pc.unavailable') });
    } catch {
      setState({ k: 'error', detail: t('pc.unavailable') });
    }
  }

  const pkg = cfg?.packages[pkgIdx];

  return (
    <div data-testid="pomela-checkout" data-state={state.k} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* header line — what is being bought (server-priced) */}
      <div>
        <h2 id="pomela-checkout-title" style={titleStyle}>
          {mode === 'pro' ? t('pc.proTitle') : t('pc.topupTitle')}
        </h2>
        <p style={subStyle}>
          {mode === 'pro'
            ? t('pc.proLine', { v: cfg?.proPriceUsd ?? 19 })
            : pkg
              ? t('pc.pkgLine', { n: pkg.credits.toLocaleString(), v: pkg.priceUsd })
              : t('pc.topupSub')}
        </p>
      </div>

      {/* package selector — canonical packages from the SERVER, never hand-typed */}
      {mode === 'topup' && cfg && (state.k === 'ready' || state.k === 'sdk' || state.k === 'fallback') && (
        <div role="radiogroup" aria-label={t('pc.choosePack')} style={{ display: 'flex', gap: 8 }}>
          {cfg.packages.map((p, i) => (
            <button
              key={p.credits}
              type="button"
              role="radio"
              aria-checked={i === pkgIdx}
              onClick={() => setPkgIdx(i)}
              style={{ ...pkgBtn, ...(i === pkgIdx ? pkgBtnActive : null) }}
            >
              <span style={{ fontSize: 15, fontWeight: 700 }}>{p.credits.toLocaleString()}</span>
              <span style={{ fontSize: 11.5, color: i === pkgIdx ? 'var(--color-primary)' : 'var(--fg-muted)' }}>${p.priceUsd}</span>
            </button>
          ))}
        </div>
      )}

      {/* state surfaces */}
      {(state.k === 'config' || state.k === 'sdk') && (
        <div style={centerBox} aria-busy="true">
          <Loader2 size={18} className="animate-spin" />
          <span style={mutedText}>{t('pc.loading')}</span>
        </div>
      )}

      {/* PayPal's OWN buttons render here (PayPal + PayPal-hosted card). */}
      <div ref={btnHost} style={{ display: state.k === 'ready' ? 'block' : 'none', minHeight: 90 }} />

      {state.k === 'ready' && (
        <p style={fineText}>
          <ShieldCheck size={12} style={{ verticalAlign: -2 }} /> {t('pc.payNote')}
        </p>
      )}

      {state.k === 'fallback' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={mutedText}>{t('pc.fallbackNote')}</p>
          <button type="button" onClick={fallbackRedirect} style={primaryBtn}>
            {t('pc.fallbackCta')} <ExternalLink size={14} />
          </button>
        </div>
      )}

      {state.k === 'verifying' && (
        <div style={centerBox} aria-busy="true">
          <Loader2 size={18} className="animate-spin" />
          <span style={mutedText}>{t('pc.verifying')}</span>
        </div>
      )}

      {state.k === 'success' && (
        <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
          <div style={successBadge}><Check size={22} /></div>
          <p style={{ fontSize: 16, fontWeight: 700, margin: '12px 0 4px' }}>
            {state.done.kind === 'topup-granted' && t('pc.credited', { n: (state.done.credited ?? 0).toLocaleString() })}
            {state.done.kind === 'topup-already' && t('pc.already')}
            {(state.done.kind === 'pro-active' || state.done.kind === 'mock-upgraded') && t('pc.proActive')}
          </p>
          {typeof state.done.remaining === 'number' && (
            <p style={mutedText}>{t('pc.balanceNow', { n: state.done.remaining.toLocaleString() })}</p>
          )}
          <button type="button" onClick={props.onClose} style={{ ...primaryBtn, marginTop: 14 }}>{t('pc.doneCta')}</button>
        </div>
      )}

      {state.k === 'webhook-pending' && (
        <div style={warnBox}>
          <TriangleAlert size={15} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ fontSize: 13.5, fontWeight: 600, margin: 0 }}>{t('pc.webhookPending')}</p>
            <p style={{ ...mutedText, margin: '4px 0 0' }}>{t('pc.webhookPendingLine')}</p>
          </div>
        </div>
      )}

      {state.k === 'failed' && (
        <div style={warnBox}>
          <TriangleAlert size={15} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ fontSize: 13.5, fontWeight: 600, margin: 0 }}>{t('pc.failed')}</p>
            <p style={{ ...mutedText, margin: '4px 0 0' }}>{t('pc.failedLine')}</p>
          </div>
        </div>
      )}

      {state.k === 'support' && (
        <div style={warnBox}>
          <TriangleAlert size={15} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <p style={{ fontSize: 13.5, fontWeight: 600, margin: 0 }}>{t('pc.supportTitle')}</p>
            <p style={{ ...mutedText, margin: '4px 0 0' }}>{t('pc.supportLine')}</p>
            <a href="#help" onClick={props.onClose} style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-primary)' }}>
              {t('pc.supportCta')}
            </a>
          </div>
        </div>
      )}

      {state.k === 'error' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={mutedText}>{state.detail}</p>
          <button type="button" onClick={fallbackRedirect} style={secondaryBtn}>
            {t('pc.fallbackCta')} <ExternalLink size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── standalone modal wrapper (CreditPanel / Upgrade use) ────────────────────

export function PomelaCheckoutModal(props: {
  mode: CheckoutMode;
  open: boolean;
  onClose: () => void;
  onDone?: (done: CheckoutDone) => void;
}): JSX.Element | null {
  useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [props.open, props.onClose]);
  if (!props.open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pomela-checkout-title"
      style={overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div style={card} class="animate-vs-slide-up">
        <button type="button" aria-label={t('common.close')} onClick={props.onClose} style={closeBtn}>
          <X size={16} />
        </button>
        <PomelaCheckoutBody mode={props.mode} onClose={props.onClose} onDone={(d) => props.onDone?.(d)} />
      </div>
    </div>
  );
}

// ─── redirect-fallback return capture (mounted once at App root) ─────────────
//
// The redirect fallback sends the buyer to PayPal with
// return_url = https://<root>/?pptopup=success — PayPal appends &token=<orderId>.
// On return we complete the SERVER capture (idempotent by orderId claim), toast the
// honest outcome, and land on #account. Without this, an approved top-up would sit
// uncaptured forever (the pre-LG2 bug: money approved, never charged, never granted).
export function TopupReturnCapture(): JSX.Element | null {
  const toast = useToast();
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const flag = q.get('pptopup');
    if (!flag) return;
    const orderId = q.get('token');
    // Clean the URL first — capture is idempotent server-side, so even a re-run
    // after an unlucky refresh cannot double-grant.
    window.history.replaceState(null, '', window.location.pathname + (window.location.hash || '#account'));
    if (!window.location.hash) window.location.hash = '#account';
    if (flag !== 'success' || !orderId) {
      if (flag === 'cancel') toast.show(t('pc.cancelled'), { variant: 'neutral' });
      return;
    }
    void (async () => {
      try {
        const r = await fetch('/v1/credits/topup/capture', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ orderId })
        });
        const j = (await r.json().catch(() => ({}))) as { ok?: boolean; credited?: number; alreadyProcessed?: boolean };
        if (r.ok && j.ok) {
          toast.show(j.alreadyProcessed ? t('pc.already') : t('pc.creditedToast', { n: (j.credited ?? 0).toLocaleString() }));
        } else {
          toast.show(t('pc.captureFailed'), { variant: 'error' });
        }
      } catch {
        toast.show(t('pc.captureFailed'), { variant: 'error' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// ─── styles (token-driven; matches the platform's modal language) ────────────

const overlay: JSX.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 95,
  display: 'grid',
  placeItems: 'end center',
  background: 'color-mix(in srgb, #0a0a0c 55%, transparent)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)'
};

const card: JSX.CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: 440,
  background: 'var(--bg)',
  color: 'var(--fg)',
  border: '1px solid var(--border)',
  borderRadius: '24px 24px 0 0',
  padding: '22px 22px 28px',
  boxShadow: '0 -24px 64px -12px rgba(0,0,0,0.32), 0 -2px 6px rgba(0,0,0,0.08)'
};

const closeBtn: JSX.CSSProperties = {
  position: 'absolute',
  top: 14,
  right: 14,
  width: 32,
  height: 32,
  borderRadius: 999,
  border: '1px solid var(--border)',
  background: 'var(--bg-subtle)',
  color: 'var(--fg-muted)',
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer'
};

const titleStyle: JSX.CSSProperties = { margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' };
const subStyle: JSX.CSSProperties = { margin: '4px 0 0', fontSize: 13.5, color: 'var(--fg-muted)' };
const mutedText: JSX.CSSProperties = { fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5 };
const fineText: JSX.CSSProperties = { fontSize: 11.5, color: 'var(--fg-subtle)', margin: 0, textAlign: 'center' };

const centerBox: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '26px 0',
  color: 'var(--fg-muted)'
};

const warnBox: JSX.CSSProperties = {
  display: 'flex',
  gap: 10,
  padding: 12,
  borderRadius: 12,
  border: '1px solid color-mix(in srgb, var(--color-warning, #F59E0B) 35%, transparent)',
  background: 'color-mix(in srgb, var(--color-warning, #F59E0B) 7%, transparent)',
  color: 'var(--fg)'
};

const successBadge: JSX.CSSProperties = {
  width: 52,
  height: 52,
  margin: '0 auto',
  borderRadius: 999,
  display: 'grid',
  placeItems: 'center',
  background: 'var(--gradient-brand)',
  color: '#fff'
};

const primaryBtn: JSX.CSSProperties = {
  width: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '12px 16px',
  borderRadius: 12,
  border: 'none',
  background: 'var(--color-primary)',
  color: 'var(--color-on-primary, #fff)',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer'
};

const secondaryBtn: JSX.CSSProperties = {
  width: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '11px 16px',
  borderRadius: 12,
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--fg)',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer'
};

const pkgBtn: JSX.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  padding: '10px 6px',
  borderRadius: 12,
  border: '1px solid var(--border)',
  background: 'var(--bg-subtle)',
  color: 'var(--fg)',
  cursor: 'pointer'
};

const pkgBtnActive: JSX.CSSProperties = {
  border: '1.5px solid var(--color-primary)',
  background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)'
};
